/* ============================================================
   sb-withdrawal.js — Widerrufsformular (sections/sb-withdrawal-form)
   ============================================================
   Progressive Enhancement ueber Shopifys Kontaktformular. Ohne
   dieses Skript — oder wenn eine der benoetigten Browser-APIs
   fehlt, oder wenn settings.sb_hub_origin leer ist — bleibt das
   native Formular stehen und postet nach /contact. Der Widerruf
   geht dann als Kontaktnachricht an den Shop; die Section
   rendert dafuer den posted_successfully-Block.

   Mit Skript wird das Absenden abgefangen, clientseitig geprueft
   und an <hub>/api/v1/withdrawal geschickt. Antwortet der Hub mit
   ok, erscheint der Erfolgsblock samt Eingangszeit, Referenz und
   der Widerrufserklaerung zum Kopieren. Antwortet er nicht,
   erscheint der Fallback-Block: das Formular bleibt mit allen
   Eingaben stehen und laesst sich per Knopf nativ absenden.

   Vier Dinge, die beim Anfassen zwingend mitbedacht gehoeren:

   1. Idempotenz-Guard plus shopify:section:load. Eine Datei in
      assets/ laeuft genau einmal; ohne den Listener ist die
      Section nach dem ersten Setting-Wechsel im Theme-Editor tot
      (CLAUDE.md → JavaScript). Zusaetzlich der readyState-Check,
      weil `defer` das Skript vor DOMContentLoaded ausfuehren
      kann, wenn die Section per Ajax nachgeladen wurde.
   2. Der Fallback-Knopf setzt form.dataset.native = '1'. Der
      Submit-Handler laesst den Post dann durch, statt ihn erneut
      abzufangen — sonst gaebe es keinen Weg zurueck ins native
      Formular.
   3. Kein Sentry, kein console.error im Fehlerpfad. Ein nicht
      erreichbarer Hub ist ein Nutzerpfad mit sichtbarer
      Alternative, keine Panne. Diagnose gibt es nur mit
      ?sb_debug=1 als console.warn.
   4. Die Validierung bildet die Rechtsvorgabe ab: Name und
      E-Mail sind Pflicht, dazu eine Vertragskennung —
      Bestellnummer ODER (Bestelldatum UND Ware). Ein Grund ist
      nie Pflicht. Wer das zu "alles Pflicht" verschaerft, macht
      die Funktion rechtswidrig.
   ============================================================ */

(function () {
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var TIMEOUT_MS = 15000;
  var DEBUG = /[?&]sb_debug=1(?:&|$)/.test(window.location.search);

  function warn(message, detail) {
    if (DEBUG && window.console && window.console.warn) {
      window.console.warn('[sb-withdrawal] ' + message, detail);
    }
  }

  function readI18n(root) {
    var node = root.querySelector('script[type="application/json"][data-sbwf="i18n"]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      warn('i18n-Block nicht lesbar', e);
      return null;
    }
  }

  function init(root) {
    if (!root || root.dataset.init) return;
    root.dataset.init = '1';

    var form = root.querySelector('form');
    if (!form) return;

    var hub = (root.getAttribute('data-hub-origin') || '').replace(/\/+$/, '');
    var lang = root.getAttribute('data-locale') === 'en' ? 'en' : 'de';
    var t = readI18n(root);

    // Ohne Ziel oder ohne die noetigen APIs bleibt das native Formular.
    // Kein Abfangen, kein Knopf-Umbau — der Nutzer merkt nichts davon.
    if (!hub || !t || !window.fetch || !window.Promise || !window.AbortController) {
      warn('kein Enhancement', { hub: hub, i18n: !!t });
      return;
    }

    function hook(name) {
      return root.querySelector('[data-sbwf="' + name + '"]');
    }

    function field(name) {
      return root.querySelector('[data-sbwf-field="' + name + '"]');
    }

    function val(name) {
      var el = field(name);
      return el ? String(el.value || '').trim() : '';
    }

    var submitBtn = hook('submit');
    var submitLabel = submitBtn ? submitBtn.textContent : '';
    var errorBox = hook('errors');
    var sending = false;

    // ── Fehlerliste ────────────────────────────────────────
    function clearErrors() {
      ['name', 'email', 'orderNumber', 'orderDate', 'goods'].forEach(function (name) {
        var el = field(name);
        if (el) el.removeAttribute('aria-invalid');
      });
      if (!errorBox) return;
      while (errorBox.firstChild) errorBox.removeChild(errorBox.firstChild);
    }

    function renderErrors(errors) {
      clearErrors();
      if (!errors.length || !errorBox) return;

      var title = document.createElement('p');
      title.className = 'sbwf__errors-title';
      title.textContent = t.errorsTitle;
      errorBox.appendChild(title);

      var list = document.createElement('ul');
      list.className = 'sbwf__errors-list';

      errors.forEach(function (entry) {
        var item = document.createElement('li');
        var target = field(entry.field);
        if (target) {
          target.setAttribute('aria-invalid', 'true');
          var link = document.createElement('a');
          link.href = '#' + target.id;
          link.textContent = entry.message;
          item.appendChild(link);
        } else {
          item.textContent = entry.message;
        }
        list.appendChild(item);
      });

      errorBox.appendChild(list);

      var first = field(errors[0].field);
      if (first && first.focus) first.focus();
    }

    // ── Validierung ────────────────────────────────────────
    function validate() {
      var errors = [];

      if (!val('name')) {
        errors.push({ field: 'name', message: t.errName });
      }

      if (!EMAIL_RE.test(val('email'))) {
        errors.push({ field: 'email', message: t.errEmail });
      }

      // Vertragskennung: Bestellnummer ODER Bestelldatum + Ware.
      if (!val('orderNumber') && !(val('orderDate') && val('goods'))) {
        errors.push({ field: 'orderNumber', message: t.errContract });
      }

      return errors;
    }

    // ── Busy-State ─────────────────────────────────────────
    function busy(on) {
      sending = on;
      if (!submitBtn) return;
      submitBtn.disabled = on;
      submitBtn.textContent = on ? t.busy : submitLabel;
    }

    // ── Datum ──────────────────────────────────────────────
    function formatMoment(date) {
      try {
        return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'de-DE', {
          dateStyle: 'long',
          timeStyle: 'medium',
          timeZone: 'Europe/Berlin',
        }).format(date);
      } catch (e) {
        return date.toISOString();
      }
    }

    function formatDay(iso) {
      var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
      if (!parts) return iso || '';
      var date = new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])));
      try {
        return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'de-DE', {
          dateStyle: 'long',
          timeZone: 'UTC',
        }).format(date);
      } catch (e) {
        return iso;
      }
    }

    function parseMoment(raw) {
      var date = new Date(raw);
      // Der Vertrag verlangt eine Zeitzone im String. Fehlt sie oder ist
      // der Wert Unsinn, ist die eigene Uhr immer noch besser als eine
      // leere Zeile in der Eingangsbestaetigung.
      if (!raw || isNaN(date.getTime())) {
        warn('receivedAt unbrauchbar', raw);
        return new Date();
      }
      return date;
    }

    // ── Erklaerungstext ────────────────────────────────────
    function buildDeclaration(payload, ref, receivedText) {
      var d = t.decl;
      var lines = [d.intro + ' ' + (payload.itemDescription || d.goodsMissing), ''];

      function push(label, value) {
        if (value) lines.push(label + ' ' + value);
      }

      push(d.orderNumber, payload.orderRef);
      push(d.orderDate, formatDay(payload.orderDate));
      push(d.name, payload.name);
      push(d.email, payload.email);
      push(d.message, payload.message);
      push(d.ref, ref);
      push(d.received, receivedText);

      return lines.join('\n');
    }

    // ── Ergebnis ───────────────────────────────────────────
    function showSuccess(payload, data) {
      var received = parseMoment(data.receivedAt);
      var receivedText = formatMoment(received);

      var timeEl = hook('received');
      if (timeEl) {
        timeEl.textContent = receivedText;
        timeEl.setAttribute('datetime', received.toISOString());
      }

      // Vertrag 3.8: Antwort {ok, id, receivedAt, reference}; `ref` bleibt als
      // Fallback fuer eine aeltere Hub-Fassung lesbar.
      var refValue = data.reference ? String(data.reference) : (data.ref ? String(data.ref) : '');
      var refEl = hook('ref');
      if (refEl) refEl.textContent = refValue;

      var declaration = hook('declaration');
      if (declaration) declaration.value = buildDeclaration(payload, refValue, receivedText);

      clearErrors();
      form.hidden = true;

      var panel = hook('success');
      if (panel) {
        panel.hidden = false;
        panel.focus();
      }
    }

    function showFallback(reason) {
      warn('Fallback auf das Kontaktformular', reason);
      clearErrors();

      var panel = hook('fallback');
      if (!panel) return;
      panel.hidden = false;
      panel.focus();
    }

    // ── Senden ─────────────────────────────────────────────
    function send() {
      // Feldnamen nach Vertrag 3.8 (Plan cookie-banner-eigenbau.md): orderRef
      // und itemDescription — die DOM-Hooks (data-sbwf-field) heissen intern
      // weiter orderNumber/goods, nur der Draht zum Hub traegt die Vertragsnamen.
      var payload = {
        name: val('name'),
        email: val('email'),
        orderRef: val('orderNumber'),
        orderDate: val('orderDate'),
        itemDescription: val('goods'),
        message: val('message'),
        locale: lang,
        website: val('website'),
      };

      var controller = new AbortController();
      var timer = setTimeout(function () {
        controller.abort();
      }, TIMEOUT_MS);
      var settled = false;

      function finish(fn) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        busy(false);
        fn();
      }

      busy(true);

      fetch(hub + '/api/v1/withdrawal', {
        method: 'POST',
        mode: 'cors',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (data) {
              return { ok: response.ok, status: response.status, data: data };
            });
        })
        .then(function (result) {
          if (result.ok && result.data && result.data.ok) {
            finish(function () {
              showSuccess(payload, result.data);
            });
          } else {
            finish(function () {
              showFallback({ status: result.status, data: result.data });
            });
          }
        })
        .catch(function (error) {
          finish(function () {
            showFallback(error);
          });
        });
    }

    // ── Absenden abfangen ──────────────────────────────────
    form.addEventListener('submit', function (event) {
      // Vom Fallback-Knopf ausgeloest: durchlassen, nicht abfangen.
      if (form.dataset.native === '1') return;

      event.preventDefault();
      if (sending) return;

      var errors = validate();
      if (errors.length) {
        renderErrors(errors);
        return;
      }
      clearErrors();

      // Honeypot gefuellt: nicht an den Hub, aber auch nicht stillschweigend
      // schlucken — der Weg ueber das Kontaktformular bleibt offen.
      if (val('website')) {
        showFallback('honeypot');
        return;
      }

      send();
    });

    var fallbackBtn = hook('fallback-submit');
    if (fallbackBtn) {
      fallbackBtn.addEventListener('click', function () {
        form.dataset.native = '1';
        form.hidden = false;
        if (form.requestSubmit) {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    }

    // ── Kopieren ───────────────────────────────────────────
    function flash(button, text) {
      var original = button.getAttribute('data-sbwf-label') || button.textContent;
      button.setAttribute('data-sbwf-label', original);
      button.textContent = text;
      clearTimeout(button.sbwfFlashTimer);
      button.sbwfFlashTimer = setTimeout(function () {
        button.textContent = original;
      }, 2500);
    }

    function legacyCopy(source, button) {
      var ok = false;
      try {
        source.focus();
        source.select();
        if (source.setSelectionRange) source.setSelectionRange(0, source.value.length);
        ok = document.execCommand('copy');
      } catch (e) {
        ok = false;
      }
      flash(button, ok ? t.copied : t.copyFailed);
    }

    var copyBtn = hook('copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var source = hook('declaration');
        if (!source) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(source.value).then(
            function () {
              flash(copyBtn, t.copied);
            },
            function () {
              legacyCopy(source, copyBtn);
            }
          );
          return;
        }

        legacyCopy(source, copyBtn);
      });
    }
  }

  function initAll(scope) {
    var root = scope || document;
    if (!root.querySelectorAll) return;
    root.querySelectorAll('[data-section-type="sb-withdrawal-form"]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll();
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  if (document.readyState !== 'loading') initAll();
})();
